import { mutate } from 'swr';
import { stringify } from 'query-string';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import request, { fetcher, fetchInfinite } from '@dashboard/services/request';
import { apiUrl } from '@dashboard/services/env';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getSellerCheckoutFromUrl from '@dashboard/services/checkout/getSellerCheckoutFromUrl';
import { pageKey, pagesKey } from '@dashboard/lib/queryKeys';

export const useDeletePage = (sellerId, token) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: pageId =>
      request(apiUrl + `/sellers/${sellerId}/pages/${pageId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pagesKey }),
  });
};

export const useRestorePage = () => {
  const { sellerId, token } = useAuth();

  return useMutation({
    mutationFn: ({ pageId, slug }) =>
      fetcher(`/sellers/${sellerId}/pages/${pageId}/restore`, token, {
        method: 'POST',
        body: JSON.stringify({
          slug,
        }),
      }),
  });
};

export const updatePage = async (sellerId, token, values, pageId, queryClient) => {
  try {
    const id = pageId || (values && values._id);
    const key = id ? `/sellers/${sellerId}/pages/${id}` : `/sellers/${sellerId}/pages`;

    // optimistic update
    if (queryClient && id) {
      queryClient.setQueryData(pageKey(id), old => {
        return {
          ...old,
          ...values,
        };
      });
    }

    const response = await request(apiUrl + key, token, {
      body: JSON.stringify(values),
      method: 'POST',
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    // mutate existing page or add new
    mutate(`/pages/${id ? id : response.data._id}`, response.data);

    if (queryClient) {
      queryClient.invalidateQueries({ queryKey: pageKey(id || response.data._id) });
    }

    return { data: response.data, error: null };
  } catch (error) {
    if (error && error.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save page', data: null };
  }
};

export const getPage = async (sellerId, token, pageId, query = {}) => {
  const qs = stringify(query);

  const key = `/sellers/${sellerId}/pages/${pageId}`;

  const page = await fetcher(`${key}?${qs}`, token);

  mutate(key, page);

  return page;
};

export const saveField = async (sellerId, token, values, fieldId, pageId) => {
  try {
    const key = fieldId
      ? `/sellers/${sellerId}/pages/${pageId}/fields/${fieldId}`
      : `/sellers/${sellerId}/pages/${pageId}/fields`;

    const response = await request(apiUrl + key, token, {
      method: 'POST',
      body: JSON.stringify(values),
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    return { data: response.data, error: null };
  } catch (error) {
    if (error?.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save field' };
  }
};

export const deleteField = async (sellerId, token, fieldId, pageId) =>
  request(`${apiUrl}/sellers/${sellerId}/pages/${pageId}/fields/${fieldId}`, token, {
    method: 'DELETE',
  });

export const sendTestEmail = async (sellerId, token, pageId) => {
  try {
    const key = `/sellers/${sellerId}/pages/${pageId}/send-confirmation-email`;

    const response = await request(apiUrl + key, token, {
      method: 'POST',
    });

    if (!response) {
      const error = new Error(response.message);
      error.response = response;
      throw error;
    }

    if (response.error) throw new Error(response.error);

    mutate(key, null);

    return true;
  } catch (error) {
    // no-op
  }
};

// react-query
async function getPages(token, sellerId, query) {
  const res = await fetchInfinite(`/sellers/${sellerId}/pages?${stringify(query)}`, token);

  return res;
}

async function getPageCount(token, sellerId) {
  const key = `/sellers/${sellerId}/pages/count`;

  const response = await request(apiUrl + key, token, {
    method: 'GET',
  });

  if (!response) {
    const error = new Error(response.message);
    error.response = response;
    throw error;
  }

  if (response.error) throw new Error(response.error);

  return response;
}

async function getPageByUrl(token, url) {
  const [seller, checkout] = getSellerCheckoutFromUrl(url);

  if (!seller || !checkout) return null;

  return fetcher(`/sellers/${seller}/pages/${checkout}`, token);
}

export default function usePageQueries() {
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();

  return {
    getPageByUrl: url => getPageByUrl(token, url),
    getPage: (pageId, query) => getPage(sellerId, token, pageId, query),
    getPages: (sellerId, query) => getPages(token, sellerId, query),
    getPageCount: sellerId => getPageCount(token, sellerId),
    updatePage: (values, pageId) => updatePage(sellerId, token, values, pageId, queryClient),
    useDeletePage: () => useDeletePage(sellerId, token),
    saveField: (values, fieldId, pageId) => saveField(sellerId, token, values, fieldId, pageId),
    deleteField: (fieldId, pageId) => deleteField(sellerId, token, fieldId, pageId),
    sendTestEmail: pageId => sendTestEmail(sellerId, token, pageId),
  };
}
